<template>
  <div class="schedule schedule--specialist background-gray min-height-inherit">
    <section class="py-4 py-md-5">
      <div class="container container--md">
        <div class="mb-3 d-none d-lg-block">
          <schedule-breadcrumb />
        </div>
        <ValidationObserver
          ref="observer"
          v-slot="{}"
          tag="form"
          @submit.prevent="handleSubmit"
        >
          <div class="schedule-details box box-sm-none">
            <div class="schedule-details__header">
              <h3 class="title title--main mb-2 mb-lg-0 mr-lg-4">
                Buscar por:
              </h3>
              <div class="schedule-details__menu">
                <router-link
                  :to="{
                    name: 'schedule-date',
                    service: current_schedule.service_slug,
                  }"
                  class="schedule-details__nav button button--default"
                >
                  <span class="button__text">Horario</span>
                </router-link>
                <router-link
                  :to="{
                    name: 'schedule-specialist',
                    service: current_schedule.service_slug,
                  }"
                  class="schedule-details__nav button button--secondary"
                >
                  <span class="button__text">Asesor de servicio</span>
                </router-link>
              </div>
            </div>
            <div v-if="current_step === 0">
              <p class="title title--s text-primary weight-bold mb-3">
                Selecciona el asesor de servicio:
              </p>
              <ValidationProvider
                name="especialista"
                vid="specialist"
                rules="required"
                v-slot="{ classes, errors }"
                tag="div"
              >
                <div :class="[classes, 'specialists']">
                  <div class="specialists__list" v-if="specialists.length">
                    <div
                      class="card-specialist mb-3 last transition"
                      v-for="specialist in specialists"
                      :key="specialist.id"
                      @click="onSetSpecialist(specialist)"
                    >
                      <div class="text">
                        <p class="weight-bolder text-primary mb-0">
                          {{ specialist.name }} {{ specialist.lastname }}
                        </p>
                        <p>{{ specialist.description }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text text-primary text-center weight-bold">
                    <i class="material-icons">assignment_ind</i>
                    <p>
                      Actualmente no tenemos ningún asesor de servicio
                      disponible
                    </p>
                  </div>
                </div>
                <span class="form-helper form-helper--error mt-1">{{
                  errors[0]
                }}</span>
              </ValidationProvider>
            </div>
            <div v-if="current_step === 1">
              <p
                class="title title--s text-primary text-center weight-bold mb-3"
              >
                Elige un día para el asesor de servicio:
                <a class="text-underline" href="#" @click.prevent="resetData">
                  {{ currentSpecialistName }}
                </a>
                <span class="text-light ml-2">(Editar)</span>
              </p>
              <div class="row">
                <div class="col-md-6">
                  <ValidationProvider
                    name="día"
                    vid="date"
                    rules="required"
                    v-slot="{ classes, errors }"
                    tag="div"
                  >
                    <datepicker
                      :class="[classes, 'datepicker']"
                      id="date"
                      v-model="data.day"
                      maximum-view="day"
                      :full-month-name="true"
                      :inline="true"
                      :language="calendar_language"
                      :disabled-dates="calendar_disabled"
                      :monday-first="true"
                      :use-utc="false"
                      @input="onSetDate"
                      @changedMonth="handleGetAvailableDates"
                    />
                    <span class="form-helper form-helper--error mt-1">{{
                      errors[0]
                    }}</span>
                  </ValidationProvider>
                </div>
                <div class="col-md-6 mt-3 mt-lg-0">
                  <ValidationProvider
                    name="horario"
                    vid="hour"
                    rules="required"
                    v-slot="{ classes, errors }"
                    tag="div"
                  >
                    <div :class="[classes, 'timepicker box']">
                      <div class="timepicker__header text-primary">
                        <i class="material-icons">schedule</i>
                        <h4 class="title title--xs weight-bold">
                          {{
                            hours.length
                              ? `Tenemos ${hours.length} horarios disponibles`
                              : 'Horarios disponibles'
                          }}
                        </h4>
                      </div>
                      <div class="timepicker__content">
                        <!-- <p class="text text-light weight-bold mb-3">
                          Nuestro horario de atención es de lunes a viernes de
                          09:00 a 18:30.
                        </p>-->
                        <div
                          class="timepicker__hours scroll"
                          v-if="hours.length"
                        >
                          <div class="scroll__content">
                            <div
                              class="mb-2 last transition"
                              v-for="hour in hours"
                              :key="hour.id"
                              @click="onSetHour(hour)"
                            >
                              <card-hour
                                :class="{
                                  'card-hour--disabled': hour.disabled,
                                  'card-hour--selected':
                                    !hour.disabled && hour.id === data.hour.id,
                                }"
                                :data="hour"
                                v-model="data.hour"
                              ></card-hour>
                            </div>
                          </div>
                          <div class="scroll__shadow"></div>
                        </div>
                        <div
                          v-else
                          class="text text-primary text-center weight-bold"
                        >
                          <i class="material-icons">schedule</i>
                          <p>
                            Selecciona un día para revisar los horarios
                            disponibles
                          </p>
                        </div>
                      </div>
                    </div>
                    <span class="form-helper form-helper--error mt-1">{{
                      errors[0]
                    }}</span>
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
          <div v-if="current_step === 1" class="text-center mt-4">
            <button class="button button--secondary" type="submit">
              <span class="button__text">Confirmar hora</span>
            </button>
          </div>
        </ValidationObserver>
      </div>
    </section>
  </div>
</template>

<script>
import {
  getAvailableSpecialists,
  getAvailableDates,
  getAvailableHours,
} from '@/services/dates';

import moment from 'moment';
import Datepicker from 'vuejs-datepicker';
import { es } from 'vuejs-datepicker/dist/locale';

import ScheduleBreadcrumb from '@/components/ScheduleBreadcrumb.vue';
import CardHour from '@/components/CardHour.vue';

import { mapState } from 'vuex';

export default {
  name: 'ScheduleSpecialist',
  components: {
    Datepicker,
    ScheduleBreadcrumb,
    CardHour,
  },
  data() {
    return {
      current_step: 0,
      calendar_language: es,
      calendar_disabled: {
        // to: new Date(),
        customPredictor: date => {
          const newDate = moment(date);
          return !this.days.includes(this.dateFormat(newDate));
        },
      },
      data: {
        specialist: '',
        day: '',
        hour: '',
      },
      specialists: [],
      days: [],
      hours: [],
      loading: false,
    };
  },
  mounted() {
    this.$store.commit('set_title', 'Estás agendando');
    this.$store.dispatch('setCurrentStep', 3);

    // if (this.current_schedule.date) {
    //   this.data.day = this.current_schedule.date.day;
    //   this.data.hour = this.current_schedule.date.hour;
    //   this.handleGetAvailableDates(this.data.day);
    //   this.onSetDate();
    //   return;
    // }

    this.handleGetAvailableSpecialists();
  },
  computed: {
    ...mapState(['current_schedule']),

    currentSpecialistName() {
      const specialist = this.specialists.find(
        o => o.id === this.data.specialist.id,
      );
      return `${specialist.name} ${specialist.lastname}`;
    },
  },
  methods: {
    async handleSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      this.submitForm();
    },
    submitForm() {
      this.loading = true;
      this.$store
        .dispatch('setSelectedSpecialist', this.data)
        .then(() => this.$router.push({ name: 'schedule-summary' }))
        .catch(() => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Ha ocurrido un error',
            text: 'No hemos podido registrar la hora seleccionada',
          });
        })
        .then(() => {
          this.loading = false;
        });
    },
    handleGetAvailableSpecialists() {
      this.getAvailableSpecialistsFromApi()
        .then(specialists => (this.specialists = specialists))
        .catch(() => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Ha ocurrido un error',
            text: 'No hemos podido obtener los asesores de servicio',
          });
        });
    },
    handleGetAvailableDates($event) {
      const current_date = $event || new Date();
      const from_date = moment(current_date).startOf('month');
      const to_date = moment(current_date).endOf('month');

      this.getAvailableDatesFromApi(
        this.dateFormat(from_date),
        this.dateFormat(to_date),
        this.data.specialist.id,
      )
        .then(days => (this.days = days))
        .catch(() => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Ha ocurrido un error',
            text: 'No hemos podido obtener las fechas',
          });
        });
    },
    onSetSpecialist(data) {
      this.data.specialist = data;
      this.current_step += 1;
      this.handleGetAvailableDates();
    },
    onSetDate() {
      this.data.hour = '';
      this.getAvailableHoursFromApi(
        this.dateFormat(this.data.day),
        this.data.specialist.id,
      )
        .then(hours => (this.hours = hours))
        .catch(() => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Ha ocurrido un error',
            text: 'No hemos podido obtener los horarios',
          });
        });
    },
    onSetHour(data) {
      this.data.hour = data;
    },
    resetData() {
      this.data = {
        specialist: '',
        day: '',
        hour: '',
      };
      this.current_step = 0;
      this.days = [];
      this.hours = [];
    },
    dateFormat(date) {
      return moment(date).format('Y-MM-DD');
    },
    async getAvailableSpecialistsFromApi() {
      const response = await getAvailableSpecialists();
      return response.data.data;
    },
    async getAvailableDatesFromApi(from, to, user) {
      const response = await getAvailableDates(from, to, user);
      return response.data.data;
    },
    async getAvailableHoursFromApi(date, user) {
      const response = await getAvailableHours(date, user);
      return response.data.data;
    },
  },
};
</script>
